import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CustomizationFlagQueries } from '@frontend/api-customization-flags';
import { checkAggregateACL } from '@frontend/auth-helpers';
import { Chips } from '@frontend/chips';
import { useTranslation } from '@frontend/i18n';
import { MultiLocationSelector } from '@frontend/multi-location-selector';
import { OrgLocationPicker } from '@frontend/org-location-picker';
import { useAppScopeStore } from '@frontend/scope';
import { TemplatesPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { FieldProps, Stepper, Text } from '@frontend/design-system';
import { overrideTextStyles, stepValueContainerStyles } from './styles';

type TemplateLocationStepProps = {
  showOrgLevelSelector: boolean;
  fieldProps: FieldProps<
    {
      locationIds: {
        type: 'checklist';
        required: boolean;
        value: string[];
        validateOnChange: boolean;
      };
    },
    'locationIds'
  >;
  next: () => void;
  previous: () => void;
  isFormModeEdit: boolean;
  isTemplateTypeReview: boolean;
  selectableLocationIds: string[];
  handleLocationsChange: (ids: string[]) => void;
};

const getSelectableLocations = ({
  selectableLocationIds,
  locationIdWiseCustomizationFlagDetails,
  aclValuesByLocationId,
  isTemplateTypeReview,
}: {
  selectableLocationIds: string[];
  locationIdWiseCustomizationFlagDetails: Record<string, boolean>;
  aclValuesByLocationId: Record<string, boolean>;
  isTemplateTypeReview: boolean;
}) => {
  const locationsWithReviewsAccess = selectableLocationIds.filter(
    (location: string) => locationIdWiseCustomizationFlagDetails[location] && aclValuesByLocationId[location]
  );

  return isTemplateTypeReview ? locationsWithReviewsAccess : selectableLocationIds;
};

export const LocationStep = ({
  fieldProps,
  next,
  isFormModeEdit,
  previous,
  isTemplateTypeReview,
  selectableLocationIds,
  showOrgLevelSelector,
  handleLocationsChange,
}: TemplateLocationStepProps) => {
  const { t } = useTranslation('templates-settings');

  const { locationIdWiseCustomizationFlagDetails } = CustomizationFlagQueries.useAggregateCustomizationFlagDetails({
    locationIds: selectableLocationIds,
    enabled: true,
    customizationFlag: Feature.REVIEWS_HOME_ICON,
  });
  const { aclValuesByLocationId } = checkAggregateACL(selectableLocationIds, [Permission.REVIEW_READ]);
  const selectableLocations = getSelectableLocations({
    selectableLocationIds,
    locationIdWiseCustomizationFlagDetails,
    aclValuesByLocationId,
    isTemplateTypeReview,
  });
  return (
    <>
      <Stepper.Title css={{ marginBottom: '14px' }}>{t('Assign Location Access')}</Stepper.Title>

      <Stepper.Content
        css={{
          display: 'flex',
          gap: '20px',
          flexDirection: 'column',
        }}
      >
        {!isFormModeEdit && (
          <Text css={{ color: theme.colors.neutral50 }}>{t('Determine which locations can use this template')}</Text>
        )}
        {showOrgLevelSelector ? (
          <OrgLocationPicker
            permission={Permission.MANUAL_MESSAGE_TEMPLATES_MANAGE}
            value={fieldProps.value}
            onChange={handleLocationsChange}
            label={t('Select Locations')}
            trackingIdPrefix={`${TemplatesPrefixes.Editor}`}
            width={350}
            showApplyButton={true}
          />
        ) : (
          selectableLocations.length > 1 && (
            <MultiLocationSelector
              accessibleLocations={selectableLocations}
              displayedLocations={selectableLocations}
              dropdownLabel={t('Select Locations')}
              fieldProps={{ locationIds: fieldProps }}
              trackingIdPrefix={`${TemplatesPrefixes.Editor}`}
            />
          )
        )}
      </Stepper.Content>

      {!isFormModeEdit && (
        <Stepper.ButtonBarAlternate>
          <Stepper.ButtonAlternate onClick={previous} position='secondary' type='none'>
            {t('Previous')}
          </Stepper.ButtonAlternate>
          <Stepper.ButtonAlternate
            onClick={() => {
              if (!!fieldProps.value.length) next();
            }}
            disabled={!fieldProps.value.length}
            position='primary'
            type='none'
          >
            {t('Next')}
          </Stepper.ButtonAlternate>
        </Stepper.ButtonBarAlternate>
      )}
    </>
  );
};

export const LocationStepCollapsed = ({ fieldProps }: Pick<TemplateLocationStepProps, 'fieldProps'>) => {
  const { t } = useTranslation('templates-settings');
  const { getLocationName } = useAppScopeStore();

  return (
    <div css={{ ...stepValueContainerStyles, flexDirection: 'column' }}>
      <Text>{t('Assign Location Access')}</Text>
      {fieldProps.value.length > 0 && (
        <>
          {fieldProps.value.length > 8 ? (
            <Text weight='regular'>{fieldProps.value.length} Locations</Text>
          ) : (
            <div
              css={{
                display: 'flex',
                gap: theme.spacing(1),
                flexWrap: 'wrap',
                maxHeight: '60px',
                overflowY: 'auto',
              }}
            >
              {...fieldProps.value.map((id, index) => (
                <Chips.LocationChip
                  key={index + '-' + id}
                  css={{
                    ...overrideTextStyles,
                    textTransform: 'initial',
                    fontSize: theme.fontSize(12),
                    '.chip': { maxWidth: 'unset' },
                  }}
                  variant='primaryDark'
                >
                  {getLocationName(id) ?? ''}
                </Chips.LocationChip>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const TemplateLocationsNonStepper = ({
  fieldProps,
  selectableLocationIds,
  isTemplateTypeReview,
}: Omit<TemplateLocationStepProps, 'next' | 'previous' | 'handleLocationsChange' | 'showOrgLevelSelector'>) => {
  const { t } = useTranslation('templates-settings');

  const { locationIdWiseCustomizationFlagDetails } = CustomizationFlagQueries.useAggregateCustomizationFlagDetails({
    locationIds: selectableLocationIds,
    enabled: true,
    customizationFlag: Feature.REVIEWS_HOME_ICON,
  });
  const { aclValuesByLocationId } = checkAggregateACL(selectableLocationIds, [Permission.REVIEW_READ]);
  const selectableLocations = getSelectableLocations({
    selectableLocationIds,
    locationIdWiseCustomizationFlagDetails,
    aclValuesByLocationId,
    isTemplateTypeReview,
  });

  if (selectableLocations.length === 0) {
    return null;
  }
  return (
    <MultiLocationSelector
      accessibleLocations={selectableLocations}
      displayedLocations={selectableLocations}
      dropdownLabel={t('Template Locations')}
      fieldProps={{ locationIds: fieldProps }}
      trackingIdPrefix={`${TemplatesPrefixes.Editor}`}
      hideRequiredLabel={!fieldProps.touched || !!fieldProps.value.length}
      requiredLabel={t('This field is required')}
      width='stretch'
    />
  );
};
